import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../ui/dialog';
import { useSnapshot } from '../../context/SnapshotContext';
import { QUESTIONS, computeRecommendation } from '../../lib/snapshotLogic';
import { QuestionStep } from './QuestionStep';
import { ContactStep } from './ContactStep';
import { ResultScreen } from './ResultScreen';
import { EducationalReveal } from './EducationalReveal';
import { X, ArrowLeft, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE = {
    INTRO: 'intro',
    QUESTIONS: 'questions',
    CONTACT: 'contact',
    SUBMITTING: 'submitting',
    RESULT: 'result',
};

const INITIAL_ANSWERS = {
    business_type: '',
    current_setup: [],
    main_goal: '',
    biggest_leak: '',
    lead_sources: [],
    after_enquiry: '',
    timeline: '',
    budget: '',
};

const INITIAL_CONTACT = {
    name: '',
    email: '',
    business_name: '',
    website: '',
    phone: '',
};

export function SnapshotModal() {
    const { open, closeSnapshot } = useSnapshot();

    const [stage, setStage] = useState(STAGE.INTRO);
    const [qIndex, setQIndex] = useState(0);
    const [answers, setAnswers] = useState(INITIAL_ANSWERS);
    const [contact, setContact] = useState(INITIAL_CONTACT);
    const [recommendation, setRecommendation] = useState(null);
    const [submitMeta, setSubmitMeta] = useState({ id: null, email_sent: false });
    const [error, setError] = useState(null);

    // Reset when modal reopens
    useEffect(() => {
        if (open) {
            setStage(STAGE.INTRO);
            setQIndex(0);
            setAnswers(INITIAL_ANSWERS);
            setContact(INITIAL_CONTACT);
            setRecommendation(null);
            setSubmitMeta({ id: null, email_sent: false });
            setError(null);
            if (typeof window !== 'undefined') window.scrollTo?.({ top: 0 });
        }
    }, [open]);

    const totalSteps = QUESTIONS.length + 1; // +1 for contact
    const currentStep =
        stage === STAGE.INTRO
            ? 0
            : stage === STAGE.CONTACT || stage === STAGE.SUBMITTING
                ? QUESTIONS.length + 1
                : qIndex + 1;

    const progressPct = useMemo(() => {
        if (stage === STAGE.INTRO) return 0;
        if (stage === STAGE.RESULT) return 100;
        return Math.min(100, Math.round((currentStep / totalSteps) * 100));
    }, [stage, currentStep, totalSteps]);

    const currentQuestion = QUESTIONS[qIndex];
    const currentValue = currentQuestion ? answers[currentQuestion.id] : null;

    const isCurrentValid = () => {
        if (!currentQuestion) return false;
        if (!currentQuestion.required) return true;
        if (currentQuestion.type === 'multi') return (currentValue || []).length > 0;
        return !!currentValue;
    };

    const handleStart = () => setStage(STAGE.QUESTIONS);

    const handleSelect = (id, type, value) => {
        setAnswers((prev) => {
            if (type === 'multi') {
                const arr = prev[id] || [];
                const exists = arr.includes(value);
                return {
                    ...prev,
                    [id]: exists ? arr.filter((v) => v !== value) : [...arr, value],
                };
            }
            return { ...prev, [id]: value };
        });
    };

    const handleNext = () => {
        if (qIndex < QUESTIONS.length - 1) {
            setQIndex(qIndex + 1);
        } else {
            setStage(STAGE.CONTACT);
        }
    };

    const handleBack = () => {
        if (stage === STAGE.CONTACT) {
            setStage(STAGE.QUESTIONS);
            return;
        }
        if (qIndex > 0) setQIndex(qIndex - 1);
        else setStage(STAGE.INTRO);
    };

    const handleSubmit = async () => {
        setError(null);
        if (!contact.name.trim() || !contact.email.trim()) {
            setError('Please share your name and email so we can send you the snapshot.');
            return;
        }
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email);
        if (!emailOk) {
            setError('That email does not look right. Please check it.');
            return;
        }

        setStage(STAGE.SUBMITTING);
        const rec = computeRecommendation(answers);
        setRecommendation(rec);

        try {
            const payload = {
                contact: {
                    name: contact.name.trim(),
                    email: contact.email.trim(),
                    business_name: contact.business_name || null,
                    website: contact.website || null,
                    phone: contact.phone || null,
                },
                answers,
                recommendation: {
                    primary_system: rec.primary_system,
                    secondary_system: rec.secondary_system,
                    main_bottleneck: rec.main_bottleneck,
                    build_path: rec.build_path,
                    lead_score: rec.lead_score,
                    next_steps: rec.next_steps,
                    explanation: rec.explanation,
                },
            };
            const res = await axios.post(`${API}/snapshots`, payload, { timeout: 15000 });
            setSubmitMeta({ id: res.data.id, email_sent: !!res.data.email_sent });
        } catch (err) {
            // Degrade gracefully: still show result, but surface note
            setSubmitMeta({ id: null, email_sent: false, saved_failed: true });
        }
        setStage(STAGE.RESULT);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && closeSnapshot()}>
            <DialogContent
                data-testid="snapshot-modal"
                className="!max-w-3xl !w-[95vw] !max-h-[92vh] overflow-hidden !p-0 !border-0 !bg-transparent !shadow-none"
            >
                <div
                    className="relative overflow-hidden"
                    style={{
                        background: 'linear-gradient(160deg, rgba(11,18,33,0.96), rgba(5,10,21,0.96))',
                        border: '1px solid rgba(255,255,255,0.10)',
                        borderRadius: '1.25rem',
                        boxShadow: '0 60px 120px -30px rgba(6,182,212,0.30), 0 0 0 1px rgba(6,182,212,0.10) inset',
                    }}
                >
                    {/* Header */}
                    <div
                        className="relative flex items-center justify-between px-5 md:px-7 py-3.5 border-b"
                        style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                    >
                        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
                            <span className="text-cyan-400">FORM 02</span>
                            <span className="text-slate-400">Growth System Snapshot</span>
                        </div>
                        <button
                            data-testid="snapshot-close-button"
                            onClick={closeSnapshot}
                            aria-label="Close"
                            className="w-7 h-7 grid place-items-center rounded-md hover:bg-white/10 hover:border-white/30 text-slate-300 hover:text-white transition"
                            style={{ border: '1px solid rgba(255,255,255,0.10)' }}
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {/* Progress */}
                    {stage !== STAGE.INTRO && stage !== STAGE.RESULT && (
                        <div
                            className="relative px-5 md:px-7 py-3 border-b"
                            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                        >
                            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.22em]">
                                <span data-testid="snapshot-step-indicator" className="text-slate-400">
                                    Step {currentStep} of {totalSteps}
                                </span>
                                <span className="text-cyan-400 tabular-nums">{progressPct}%</span>
                            </div>
                            <div
                                className="mt-2 h-[3px] rounded-full"
                                style={{ background: 'rgba(255,255,255,0.06)' }}
                            >
                                <div
                                    className="h-full rounded-full transition-all duration-700"
                                    style={{
                                        width: `${progressPct}%`,
                                        background: 'linear-gradient(90deg, #06B6D4, #3B82F6)',
                                        boxShadow: '0 0 12px rgba(6,182,212,0.5)',
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Body */}
                    <div className="relative max-h-[calc(92vh-150px)] overflow-y-auto px-5 md:px-8 py-7">
                        {stage === STAGE.INTRO && <IntroScreen onStart={handleStart} />}

                        {stage === STAGE.QUESTIONS && currentQuestion && (
                            <QuestionStep
                                question={currentQuestion}
                                value={currentValue}
                                onSelect={handleSelect}
                            />
                        )}

                        {stage === STAGE.CONTACT && (
                            <ContactStep
                                contact={contact}
                                onChange={setContact}
                                error={error}
                            />
                        )}

                        {stage === STAGE.SUBMITTING && <SubmittingScreen />}

                        {stage === STAGE.RESULT && recommendation && (
                            <>
                                <ResultScreen
                                    contact={contact}
                                    recommendation={recommendation}
                                    submitMeta={submitMeta}
                                    onClose={closeSnapshot}
                                />
                                <EducationalReveal onClose={closeSnapshot} />
                            </>
                        )}
                    </div>

                    {/* Footer nav */}
                    {(stage === STAGE.QUESTIONS || stage === STAGE.CONTACT) && (
                        <div
                            className="relative flex items-center justify-between gap-3 px-5 md:px-7 py-4 border-t"
                            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                        >
                            <button
                                data-testid="snapshot-back-button"
                                type="button"
                                onClick={handleBack}
                                className="btn-ghost !py-2 !px-3.5 !text-[13px]"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                Back
                            </button>

                            {stage === STAGE.QUESTIONS && (
                                <button
                                    data-testid="snapshot-next-button"
                                    type="button"
                                    onClick={handleNext}
                                    disabled={!isCurrentValid()}
                                    className="btn-primary !py-2 !px-4 !text-[13px] disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    {qIndex === QUESTIONS.length - 1 ? 'Continue' : 'Next'}
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            )}
                            {stage === STAGE.CONTACT && (
                                <button
                                    data-testid="snapshot-generate-button"
                                    type="button"
                                    onClick={handleSubmit}
                                    className="btn-primary !py-2 !px-4 !text-[13px]"
                                >
                                    Generate My Snapshot
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function IntroScreen({ onStart }) {
    return (
        <div data-testid="snapshot-intro" className="py-2 md:py-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-400">
                Diagnostic · ~ 2 minutes
            </p>
            <h2 className="mt-4 font-display font-semibold text-[2.4rem] md:text-[3.4rem] leading-[1.0] tracking-[-0.04em] text-white">
                Most businesses are{' '}
                <span className="text-gradient-cyan">losing clients</span>
                <br />
                without realising it.
            </h2>
            <p className="mt-5 text-[17px] leading-[1.6] text-slate-300 max-w-xl">
                Answer a few quick questions to find where your system is
                leaking — and what to fix first.
            </p>

            <ol
                className="mt-8 grid grid-cols-2 md:grid-cols-4 border-t border-b"
                style={{ borderColor: 'rgba(255,255,255,0.08)' }}
            >
                {[
                    { k: '01', l: 'Answer questions' },
                    { k: '02', l: 'Diagnose leak' },
                    { k: '03', l: 'Get next steps' },
                    { k: '04', l: 'Build path' },
                ].map((s, i) => (
                    <li
                        key={s.k}
                        className="py-4 px-4 first:pl-0 list-none"
                        style={{
                            borderRight:
                                i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                        }}
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">
                            {s.k}
                        </p>
                        <p className="mt-1.5 font-display font-medium text-[16px] tracking-[-0.02em] text-white">
                            {s.l}
                        </p>
                    </li>
                ))}
            </ol>

            <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                    data-testid="snapshot-start-button"
                    onClick={onStart}
                    className="btn btn-primary"
                >
                    Begin diagnostic
                    <ArrowRight className="w-4 h-4" />
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    No contact details required to begin
                </span>
            </div>
        </div>
    );
}

function SubmittingScreen() {
    return (
        <div
            data-testid="snapshot-submitting"
            className="py-16 text-center flex flex-col items-center gap-5"
        >
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-400">
                Compiling
            </div>
            <div className="flex items-center gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                    <span
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{
                            background: 'linear-gradient(90deg, #06B6D4, #3B82F6)',
                            animation: `dot-bounce 1s ease-in-out ${i * 0.12}s infinite`,
                            opacity: 0.4,
                        }}
                    />
                ))}
            </div>
            <p className="font-display font-semibold text-[26px] tracking-[-0.03em] text-white">
                Generating your snapshot…
            </p>
            <p className="text-[15px] text-slate-400">
                Running rule-based diagnostics on your answers.
            </p>
            <style>{`@keyframes dot-bounce { 0%,100%{opacity:0.3;transform:translateY(0)} 50%{opacity:1;transform:translateY(-4px)} }`}</style>
        </div>
    );
}
